import { useRef } from "react";
import { motion, useInView } from "motion/react";

import LumientLogoMark from "@/components/LumientLogoMark";
import styles from "@/components/NativeAIOrchestrationSection.module.css";

const EASE = [0.25, 0.1, 0.25, 1] as const;

const AGENT_CAPABILITIES = [
  {
    id: "act",
    tier: "ACT",
    name: "Agents can complete bounded work when the record, policy, and required evidence are already in place.",
  },
  {
    id: "prove",
    tier: "PROVE",
    name: "Every action can be explained from the source evidence, workflow run, and audit trail behind it.",
  },
] as const;

const FOUNDATION_LABELS = ["State to act", "Evidence to trust", "Policy to control", "History to audit"] as const;

const NativeAIOrchestrationSection = () => {
  const diagramRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(diagramRef, { once: true, amount: 0.2 });

  return (
    <section
      id="native-ai-orchestration"
      className={styles.section}
      aria-labelledby="native-ai-orchestration-heading"
    >
      <div className={styles.container}>
        <motion.div
          className={styles.header}
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          viewport={{ once: true, amount: 0.35 }}
        >
          <p className={styles.eyebrow}>ENTERPRISE AI OPERATIONS</p>
          <h2
            id="native-ai-orchestration-heading"
            className={styles.heading}
            aria-label="Deploy enterprise agents on data you trust."
          >
            Deploy enterprise agents<br />
            <span className={styles.headingAccent} data-testid="native-ai-orchestration-heading-accent">
              on data you trust.
            </span>
          </h2>
          <p className={styles.copy}>
            Agents need more than retrieved context. Lumient gives them current records, proof,
            policies, and review gates, so they can operate inside evidence-heavy workflows without
            rereading raw files from scratch.
          </p>
        </motion.div>

        <motion.div
          ref={diagramRef}
          className={styles.diagramShell}
          data-testid="native-ai-orchestration-diagram"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.75, ease: EASE, delay: 0.08 }}
          viewport={{ once: true, amount: 0.2 }}
        >
          <div className={styles.diagramBezel}>
          <div className={styles.diagram}>

            {/* Agent call label */}
            <motion.p
              className={styles.agentCallLabel}
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.7, ease: EASE, delay: 0.5 }}
            >
              Enterprise agents operate through Lumient.
            </motion.p>

            {/* Orchestration node */}
            <motion.div
              className={styles.agentNode}
              initial={{ opacity: 0, y: 14 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, ease: EASE, delay: 0.75 }}
            >
              <div className={styles.agentIcon}>
                <LumientLogoMark className="h-8 w-8 text-primary" testId="native-ai-orchestration-logo" />
              </div>
              <div>
                <p className={styles.agentLabel}>Lumient</p>
                <p className={styles.agentMeta}>operational orchestration layer</p>
              </div>
            </motion.div>

            {/* Fork connectors — draw top-down, 110ms stagger between siblings */}
            <div className={styles.forkConnector} aria-hidden="true">
              <motion.span
                className={styles.forkStem}
                initial={{ scaleY: 0 }}
                animate={isInView ? { scaleY: 1 } : {}}
                transition={{ duration: 0.6, ease: EASE, delay: 1.2 }}
              />
              <motion.span
                className={styles.forkBus}
                initial={{ scaleX: 0 }}
                animate={isInView ? { scaleX: 1 } : {}}
                transition={{ duration: 0.65, ease: EASE, delay: 1.7 }}
              />
              <motion.span
                className={styles.forkDropLeft}
                initial={{ scaleY: 0 }}
                animate={isInView ? { scaleY: 1 } : {}}
                transition={{ duration: 0.65, ease: EASE, delay: 2.2 }}
              />
              <motion.span
                className={styles.forkDropRight}
                initial={{ scaleY: 0 }}
                animate={isInView ? { scaleY: 1 } : {}}
                transition={{ duration: 0.65, ease: EASE, delay: 2.31 }}
              />
            </div>

            {/* Capability cards — 110ms stagger per DESIGN.md */}
            <h3 id="native-ai-agent-capabilities-heading" className="sr-only">
              Agent capabilities
            </h3>
            <div
              className={styles.toolGrid}
              role="group"
              aria-labelledby="native-ai-agent-capabilities-heading"
            >
              {AGENT_CAPABILITIES.map((tool, i) => (
                <motion.article
                  key={tool.id}
                  className={styles.toolCard}
                  initial={{ opacity: 0, y: 14 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.7, ease: EASE, delay: 2.75 + i * 0.11 }}
                >
                  <p className={styles.toolTier}>{tool.tier}</p>
                  <p className={styles.toolName}>{tool.name}</p>
                </motion.article>
              ))}
            </div>

            {/* Merge connectors — rise bottom-up, 110ms stagger between siblings */}
            <div className={styles.mergeConnector} aria-hidden="true">
              <motion.span
                className={styles.mergeRiseLeft}
                initial={{ scaleY: 0 }}
                animate={isInView ? { scaleY: 1 } : {}}
                transition={{ duration: 0.65, ease: EASE, delay: 3.3 }}
              />
              <motion.span
                className={styles.mergeRiseRight}
                initial={{ scaleY: 0 }}
                animate={isInView ? { scaleY: 1 } : {}}
                transition={{ duration: 0.65, ease: EASE, delay: 3.41 }}
              />
              <motion.span
                className={styles.mergeBus}
                initial={{ scaleX: 0 }}
                animate={isInView ? { scaleX: 1 } : {}}
                transition={{ duration: 0.65, ease: EASE, delay: 3.85 }}
              />
              <motion.span
                className={styles.mergeStem}
                initial={{ scaleY: 0 }}
                animate={isInView ? { scaleY: 1 } : {}}
                transition={{ duration: 0.65, ease: EASE, delay: 4.35 }}
              />
            </div>

            {/* Workspace */}
            <motion.div
              className={styles.workspace}
              initial={{ opacity: 0, y: 14 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, ease: EASE, delay: 4.8 }}
            >
              <motion.p
                className={styles.workspaceLabel}
                initial={{ opacity: 0, filter: "blur(6px)" }}
                animate={isInView ? { opacity: 1, filter: "blur(0px)" } : {}}
                transition={{ duration: 0.8, ease: EASE, delay: 5.05 }}
              >
                Lumient workspace
              </motion.p>
              <motion.p
                className={styles.workspaceSubcopy}
                initial={{ opacity: 0, filter: "blur(6px)" }}
                animate={isInView ? { opacity: 1, filter: "blur(0px)" } : {}}
                transition={{ duration: 0.8, ease: EASE, delay: 5.2 }}
              >
                Governed operational records where state, evidence, policy, and history stay bound.
              </motion.p>
              <motion.div
                className={styles.foundationLabels}
                role="group"
                aria-label="Workspace foundation labels"
                initial={{ opacity: 0, filter: "blur(6px)" }}
                animate={isInView ? { opacity: 1, filter: "blur(0px)" } : {}}
                transition={{ duration: 0.8, ease: EASE, delay: 5.35 }}
              >
                {FOUNDATION_LABELS.map((label) => (
                  <span key={label} className={styles.foundationPill}>
                    {label}
                  </span>
                ))}
              </motion.div>
            </motion.div>

            {/* Caption */}
            <motion.p
              className={styles.caption}
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.85, ease: EASE, delay: 5.85 }}
            >
              Operational work, with proof attached.
            </motion.p>

          </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default NativeAIOrchestrationSection;
